import apiClient from './api';

class AdminService {
  // ─── Users ──────────────────────────────────────────────────────────────────
  async getUsers(filter = {}) {
    try {
      const response = await apiClient.get('/admin/users', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch users.';
    }
  }

  async activateUser(id) {
    try {
      const response = await apiClient.put(`/admin/users/${id}/activate`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to activate user.';
    }
  }

  async deactivateUser(id) {
    try {
      const response = await apiClient.put(`/admin/users/${id}/deactivate`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to deactivate user.';
    }
  }

  async changeUserRole(id, roleId) {
    try {
      const response = await apiClient.put(`/admin/users/${id}/role`, { roleId });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to change user role.';
    }
  }

  // ─── Organizations ──────────────────────────────────────────────────────────
  async getOrganizations(filter = {}) {
    try {
      const response = await apiClient.get('/admin/organizations', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch organizations.';
    }
  }

  async overrideSubscriptionStatus(orgId, paymentStatus) {
    try {
      const response = await apiClient.put(`/admin/organizations/${orgId}/subscription/status`, { paymentStatus });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to override subscription status.';
    }
  }

  // ─── Operators ──────────────────────────────────────────────────────────────
  async getOperators(filter = {}) {
    try {
      const response = await apiClient.get('/admin/operators', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch operators.';
    }
  }

  async getOperator(id) {
    try {
      const response = await apiClient.get(`/admin/operators/${id}`);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch operator.';
    }
  }

  async updateOperator(id, dto) {
    try {
      const response = await apiClient.put(`/admin/operators/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update operator.';
    }
  }

  async createOperator(dto) {
    try {
      const response = await apiClient.post('/admin/operators', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create operator.';
    }
  }

  async deleteOperator(id) {
    try {
      await apiClient.delete(`/admin/operators/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete operator.';
    }
  }

  async bulkUploadOperators(file) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post('/admin/operators/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to bulk upload operators.';
    }
  }

  // ─── Payments ───────────────────────────────────────────────────────────────
  async getPaymentHistory(filter = {}) {
    try {
      const response = await apiClient.get('/admin/payments/history', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch payment history.';
    }
  }

  async getSubscriptions(filter = {}) {
    try {
      const response = await apiClient.get('/admin/payments/subscriptions', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch subscriptions.';
    }
  }

  // ─── Subscription Plans ─────────────────────────────────────────────────────
  async getPlans() {
    try {
      const response = await apiClient.get('/admin/subscription-plans');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch subscription plans.';
    }
  }

  async createPlan(dto) {
    try {
      const response = await apiClient.post('/admin/subscription-plans', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create subscription plan.';
    }
  }

  async updatePlan(id, dto) {
    try {
      const response = await apiClient.put(`/admin/subscription-plans/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update subscription plan.';
    }
  }

  async deletePlan(id) {
    try {
      await apiClient.delete(`/admin/subscription-plans/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete subscription plan.';
    }
  }

  // ─── Audit Logs ─────────────────────────────────────────────────────────────
  async getAuditLogs(filter = {}) {
    try {
      const response = await apiClient.get('/admin/audit-logs', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch audit logs.';
    }
  }

  // ─── Dashboard ───────────────────────────────────────────────────────────────
  async getDashboardStats() {
    try {
      const response = await apiClient.get('/admin/dashboard');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch dashboard stats.';
    }
  }

  async getExpiringSoon(days = 30, page = 1, pageSize = 25) {
    try {
      const response = await apiClient.get('/admin/payments/expiring-soon', { params: { days, page, pageSize } });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch expiring subscriptions.';
    }
  }

  // ─── Admin Products ──────────────────────────────────────────────────────────
  async getAdminProducts(filter = {}) {
    try {
      const response = await apiClient.get('/admin/products', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch products.';
    }
  }

  // ─── Admin Product Categories ────────────────────────────────────────────────
  async getAdminProductCategories(filter = {}) {
    try {
      const response = await apiClient.get('/admin/product-categories', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch product categories.';
    }
  }

  async createAdminProductCategory(dto) {
    try {
      const response = await apiClient.post('/admin/product-categories', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create product category.';
    }
  }

  async updateAdminProductCategory(id, dto) {
    try {
      const response = await apiClient.put(`/admin/product-categories/${id}`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update product category.';
    }
  }

  async deleteAdminProductCategory(id) {
    try {
      await apiClient.delete(`/admin/product-categories/${id}`);
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete product category.';
    }
  }

  // ─── Trial Requests ──────────────────────────────────────────────────────────
  async getTrialRequests(filter = {}) {
    try {
      const response = await apiClient.get('/admin/trial-requests', { params: filter });
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch trial requests.';
    }
  }

  async sendTrialCredentials(id, dto) {
    try {
      const response = await apiClient.post(`/admin/trial-requests/${id}/send-credentials`, dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to send trial credentials.';
    }
  }

  // ─── Create User ─────────────────────────────────────────────────────────────
  async createUser(dto) {
    try {
      const response = await apiClient.post('/admin/users', dto);
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create user.';
    }
  }

  // ─── States ──────────────────────────────────────────────────────────────────
  async getStates() {
    try {
      const response = await apiClient.get('/onboarding/states');
      return response.data.data ?? response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch states.';
    }
  }
}

export default new AdminService();
